import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, ShieldAlert, BookOpen, FileText, FileSignature, CreditCard,
  FolderOpen, ListMusic, Images, Star, Lightbulb, Trophy,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { GoogleLoginButton } from "@/lib/auth";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#080C14]">
    <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
      <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/admin"
          data-testid="guide-back-link"
          className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
        >
          <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Retour au tableau de bord
        </Link>
        <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
      </div>
    </header>
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">{children}</div>
  </div>
);

const SECTIONS = [
  {
    icon: FileText,
    titre: "Demandes de devis",
    texte:
      "Chaque demande envoyée depuis le formulaire du site apparaît ici en temps réel : nom du client, coordonnées, type d'événement, date, formule choisie, nombre d'invités et message libre. Vous êtes en parallèle notifié par e-mail sur contact@klmprod.com. Depuis chaque demande, un clic sur « Lien de signature » génère le contrat correspondant et copie le lien à envoyer au client.",
  },
  {
    icon: FileSignature,
    titre: "Contrats",
    texte:
      "La section Contrats suit chaque dossier : client, formule, montant de l'acompte (30 %) et statut — « En attente » ou « Signé ». Pour chaque contrat, vous pouvez copier le lien de signature à transmettre au client et télécharger le PDF du contrat, généré automatiquement avec la signature électronique une fois celle-ci effectuée. La signature en ligne déclenche ensuite directement le paiement de l'acompte par carte bancaire.",
  },
  {
    icon: CreditCard,
    titre: "Paiements",
    texte:
      "Tous les règlements en ligne sont tracés ici : acomptes de 30 % à la signature, soldes de 70 % avant l'événement. Chaque ligne indique le montant, la formule concernée, la date et le statut (Payé ou en cours). Sept jours avant l'événement, le client reçoit automatiquement un e-mail de rappel avec le lien pour régler le solde.",
  },
  {
    icon: FolderOpen,
    titre: "Fichiers clients",
    texte:
      "Vos clients déposent depuis leur espace les fichiers utiles à la préparation : diaporamas, photos, musiques, documents (jusqu'à 50 Mo). Vous les retrouvez ici classés par événement, avec le nom de l'expéditeur, la catégorie et la taille. Un clic suffit pour les télécharger. Chaque dépôt vous est signalé par e-mail sur klmprodplaylist@gmail.com.",
  },
  {
    icon: ListMusic,
    titre: "Playlists collaboratives",
    texte:
      "Toutes les playlists créées par vos clients sont listées avec leur date d'événement, le nombre de titres proposés et l'état du verrouillage. La playlist se verrouille automatiquement 48 h avant l'événement : votre programmation est figée à temps, sans aucune action de votre part. Les clients reçoivent des rappels automatiques à J-7 et J-2 avec le message prêt à transférer à leurs invités.",
  },
  {
    icon: Images,
    titre: "Galerie publique",
    texte:
      "Vous alimentez vous-même la section « Nos soirées en images » du site : ajoutez une photo avec sa légende (par exemple « Mariage — Bollène 2024 »), elle apparaît immédiatement en ligne ; supprimez celles que vous ne souhaitez plus montrer.",
  },
  {
    icon: Star,
    titre: "Avis clients",
    texte:
      "Deux sources d'avis : ceux que vous ajoutez directement (publiés immédiatement, avec la source — Google, mariages.net) et ceux envoyés par vos clients via le lien qu'ils reçoivent automatiquement le lendemain de leur soirée. Ces derniers arrivent « En attente » : vous les lisez, puis les publiez ou les supprimez d'un clic. Rien ne s'affiche sur le site sans votre accord.",
  },
];

const AVANTAGES = [
  "Zéro relance manuelle : rappels invités (J-7, J-2), rappel de solde (J-7) et demande d'avis (J+1) partent seuls",
  "Un dossier complet par événement : devis → contrat signé → acompte → fichiers → playlist → solde → avis",
  "Confidentialité préservée : seul votre compte autorisé accède à ces données ; les invités ne voient que la playlist via leur code d'accès",
  "Votre site s'enrichit seul : chaque photo et chaque avis publié renforce la confiance des prochains clients",
];

const CONSEILS = [
  "Traitez les devis sous 48 h — c'est la promesse affichée sur le site ; le tableau vous montre la date de réception de chaque demande",
  "Générez le lien de signature dès l'accord verbal : plus le contrat part tôt, plus la date est réservée vite",
  "Après chaque soirée, déposez 2-3 photos dans la galerie pendant que l'événement est frais",
  "Publiez les avis rapidement : un avis tout frais a plus d'impact, et votre client se sent écouté",
  "Consultez les fichiers clients à J-3 pour préparer sereinement discours, surprises et diaporamas avant le verrouillage",
];

export default function GuideAdmin() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Shell>
        <div className="flex h-80 items-center justify-center" data-testid="guide-loading">
          <Loader2 size={26} className="animate-spin text-cyan-400" />
        </div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <div className="mx-auto flex max-w-md flex-col items-center pt-16 text-center" data-testid="guide-login-gate">
          <BookOpen size={36} className="text-cyan-400" />
          <h1 className="font-display mt-5 text-2xl font-extrabold text-slate-50">Guide d'utilisation</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Connectez-vous avec votre compte Google professionnel pour consulter le guide.
          </p>
          <GoogleLoginButton testId="guide-login-button" className="mt-7" />
        </div>
      </Shell>
    );
  }

  if (!user.is_admin) {
    return (
      <Shell>
        <div className="mx-auto flex max-w-md flex-col items-center pt-16 text-center" data-testid="guide-forbidden">
          <ShieldAlert size={36} className="text-amber-400" />
          <h1 className="font-display mt-5 text-2xl font-extrabold text-slate-50">Accès réservé</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Ce compte ({user.email}) n'a pas les droits d'administration.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="guide-admin-content">
        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">Espace administrateur</p>
        <h1 className="font-display mt-3 text-3xl font-extrabold text-slate-50 sm:text-4xl">
          Guide <span className="text-gradient">d'utilisation.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400">
          L'espace administrateur centralise toute la gestion de votre activité en un seul écran : devis,
          contrats, paiements, fichiers, playlists, galerie et avis. Ce guide vous accompagne à chaque étape,
          de la première prise de contact jusqu'au lendemain de la soirée.
        </p>

        <div className="mt-12 space-y-4">
          {SECTIONS.map((s, i) => (
            <section
              key={s.titre}
              className="rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-6"
              data-testid={`guide-section-${i}`}
            >
              <h2 className="font-display flex items-center gap-2.5 text-lg font-bold text-slate-100">
                <s.icon size={18} className="text-cyan-400" /> {s.titre}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.texte}</p>
            </section>
          ))}
        </div>

        <section className="mt-10 rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 p-6" data-testid="guide-avantages">
          <h2 className="font-display flex items-center gap-2.5 text-lg font-bold text-slate-100">
            <Trophy size={18} className="text-cyan-300" /> Ce que vous y gagnez
          </h2>
          <ul className="mt-4 space-y-2.5">
            {AVANTAGES.map((a) => (
              <li key={a} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-300">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                {a}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-6 rounded-2xl border border-cyan-500/15 bg-[#0E1524]/70 p-6" data-testid="guide-conseils">
          <h2 className="font-display flex items-center gap-2.5 text-lg font-bold text-slate-100">
            <Lightbulb size={18} className="text-cyan-400" /> Conseils pratiques
          </h2>
          <ol className="mt-4 space-y-2.5">
            {CONSEILS.map((c, i) => (
              <li key={c} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                <span className="font-mono-brand mt-0.5 shrink-0 text-xs text-cyan-400">/{i + 1}</span>
                {c}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </Shell>
  );
}
